import { useEffect, useState } from 'react';
import { platformConfig, type Announcement } from '@/services/platform';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Modal } from '@/components/common/Modal';
import styles from './AnnouncementsPage.module.css';

type AnnouncementFormData = {
  title: string;
  content: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  startsAt: string;
  endsAt: string;
};

const PRIORITY_OPTIONS: { value: Announcement['priority']; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'var(--text-muted)' },
  { value: 'medium', label: 'Medium', color: 'var(--info)' },
  { value: 'high', label: 'High', color: 'var(--warning)' },
  { value: 'critical', label: 'Critical', color: 'var(--error)' },
];

export function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [formData, setFormData] = useState<AnnouncementFormData>({
    title: '',
    content: '',
    priority: 'medium',
    startsAt: '',
    endsAt: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  async function fetchAnnouncements() {
    setLoading(true);
    setError(null);

    try {
      const response = await platformConfig.getAnnouncements();
      if (response.success && response.data) {
        setAnnouncements(response.data);
      }
    } catch (err) {
      setError('Failed to load announcements');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function openCreateModal() {
    setEditingAnnouncement(null);
    setFormData({
      title: '',
      content: '',
      priority: 'medium',
      startsAt: new Date().toISOString().slice(0, 16),
      endsAt: '',
    });
    setFormError(null);
    setShowModal(true);
  }

  function openEditModal(announcement: Announcement) {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      priority: announcement.priority,
      startsAt: announcement.startsAt.slice(0, 16),
      endsAt: announcement.endsAt ? announcement.endsAt.slice(0, 16) : '',
    });
    setFormError(null);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingAnnouncement(null);
    setFormError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!formData.title.trim()) {
      setFormError('Title is required');
      return;
    }

    if (!formData.content.trim()) {
      setFormError('Content is required');
      return;
    }

    if (!formData.startsAt) {
      setFormError('Start date is required');
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      const payload = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        priority: formData.priority,
        startsAt: new Date(formData.startsAt).toISOString(),
        endsAt: formData.endsAt ? new Date(formData.endsAt).toISOString() : undefined,
      };

      if (editingAnnouncement) {
        const response = await platformConfig.updateAnnouncement(editingAnnouncement.id, payload);
        if (!response.success) {
          throw new Error(response.error?.message || 'Failed to update announcement');
        }
      } else {
        const response = await platformConfig.createAnnouncement(payload);
        if (!response.success) {
          throw new Error(response.error?.message || 'Failed to create announcement');
        }
      }

      closeModal();
      fetchAnnouncements();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!deleteConfirmId) return;

    setDeleting(true);
    try {
      const response = await platformConfig.deleteAnnouncement(deleteConfirmId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete announcement');
      }
      setDeleteConfirmId(null);
      fetchAnnouncements();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete announcement');
    } finally {
      setDeleting(false);
    }
  }

  async function handleToggleActive(announcement: Announcement) {
    try {
      const response = await platformConfig.updateAnnouncement(announcement.id, {
        isActive: !announcement.isActive,
      });
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update announcement');
      }
      fetchAnnouncements();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update announcement');
    }
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function getStatusLabel(announcement: Announcement): { label: string; color: string } {
    const now = new Date();
    const startsAt = new Date(announcement.startsAt);
    const endsAt = announcement.endsAt ? new Date(announcement.endsAt) : null;

    if (!announcement.isActive) {
      return { label: 'Inactive', color: 'var(--text-muted)' };
    }
    if (now < startsAt) {
      return { label: 'Scheduled', color: 'var(--info)' };
    }
    if (endsAt && now > endsAt) {
      return { label: 'Expired', color: 'var(--warning)' };
    }
    return { label: 'Active', color: 'var(--success)' };
  }

  function getPriorityInfo(priority: Announcement['priority']) {
    return PRIORITY_OPTIONS.find((p) => p.value === priority) || PRIORITY_OPTIONS[1];
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Announcements</h1>
          <p className={styles.subtitle}>Manage platform-wide announcements and notifications</p>
        </div>
        <Button onClick={openCreateModal}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New Announcement
        </Button>
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      <div className={styles.content}>
        {loading ? (
          <div className={styles.loading}>Loading announcements...</div>
        ) : announcements.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            <h3>No Announcements</h3>
            <p>Create your first announcement to broadcast messages to all institutions</p>
            <Button onClick={openCreateModal}>Create Announcement</Button>
          </div>
        ) : (
          <div className={styles.announcementsList}>
            {announcements.map((announcement) => {
              const status = getStatusLabel(announcement);
              const priorityInfo = getPriorityInfo(announcement.priority);

              return (
                <div key={announcement.id} className={styles.announcementCard}>
                  <div className={styles.cardHeader}>
                    <div className={styles.cardTitleRow}>
                      <h3 className={styles.cardTitle}>{announcement.title}</h3>
                      <div className={styles.cardBadges}>
                        <span
                          className={styles.priorityBadge}
                          style={{ color: priorityInfo.color, borderColor: priorityInfo.color }}
                        >
                          {priorityInfo.label}
                        </span>
                        <span
                          className={styles.statusBadge}
                          style={{ backgroundColor: status.color }}
                        >
                          {status.label}
                        </span>
                      </div>
                    </div>
                    <div className={styles.cardMeta}>
                      <span>Starts: {formatDate(announcement.startsAt)}</span>
                      {announcement.endsAt && <span>Ends: {formatDate(announcement.endsAt)}</span>}
                    </div>
                  </div>

                  <div className={styles.cardContent}>
                    <p>{announcement.content}</p>
                  </div>

                  <div className={styles.cardActions}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(announcement)}
                    >
                      {announcement.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => openEditModal(announcement)}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={styles.deleteBtn}
                      onClick={() => setDeleteConfirmId(announcement.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}
      >
        <form onSubmit={handleSubmit} className={styles.form}>
          {formError && <div className={styles.formError}>{formError}</div>}

          <Input
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="Enter announcement title"
            required
          />

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Content</label>
            <textarea
              className={styles.textarea}
              value={formData.content}
              onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
              placeholder="Enter announcement content..."
              rows={4}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Priority</label>
            <div className={styles.priorityOptions}>
              {PRIORITY_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`${styles.priorityOption} ${
                    formData.priority === option.value ? styles.selected : ''
                  }`}
                  style={{ borderColor: formData.priority === option.value ? option.color : undefined }}
                >
                  <input
                    type="radio"
                    name="priority"
                    value={option.value}
                    checked={formData.priority === option.value}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        priority: e.target.value as Announcement['priority'],
                      }))
                    }
                  />
                  <span style={{ color: option.color }}>{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Start Date & Time</label>
              <input
                type="datetime-local"
                className={styles.dateInput}
                value={formData.startsAt}
                onChange={(e) => setFormData((prev) => ({ ...prev, startsAt: e.target.value }))}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>End Date & Time (Optional)</label>
              <input
                type="datetime-local"
                className={styles.dateInput}
                value={formData.endsAt}
                onChange={(e) => setFormData((prev) => ({ ...prev, endsAt: e.target.value }))}
              />
            </div>
          </div>

          <div className={styles.formActions}>
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting
                ? editingAnnouncement
                  ? 'Updating...'
                  : 'Creating...'
                : editingAnnouncement
                ? 'Update Announcement'
                : 'Create Announcement'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="Delete Announcement"
      >
        <div className={styles.confirmModal}>
          <p>Are you sure you want to delete this announcement? This action cannot be undone.</p>
          <div className={styles.confirmActions}>
            <Button variant="secondary" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
